import crypto from 'crypto';
import fs from 'fs/promises';
// @ts-ignore netstorageapi doesn't has types
import Netstorage from 'netstorageapi';
import path from 'path';

import type { NetStorageCallback, NetStorageConfig } from '../types/netstorageapi';

interface Config extends NetStorageConfig {
  path: string;
  publicHost: string;
  publicPath: string;
}

interface Response {
  message: string;
}

class AkamaiStorage extends Netstorage {
  private config: Config;

  private mkdir!: (netstoragePath: string, callback: NetStorageCallback) => void;

  private upload!: (
    localSource: string,
    netstorageDestination: string,
    callback: NetStorageCallback
  ) => void;

  constructor(config: Config) {
    super(config);
    this.config = config;
  }

  getAkamaiUrl(filePath: string, nsFolder?: string) {
    const base = `https://${this.config.publicHost}`;
    const file = nsFolder
      ? `${this.config.publicPath}${nsFolder}/${filePath}`
      : `${this.config.publicPath}${filePath}`;
    return new URL(file, base).toString();
  }

  sanitizePath(filePath: string) {
    let sanitizedPath = path.normalize(filePath).replace(/^(\.\.[/\\])+/, '');
    if (!sanitizedPath.endsWith('/')) {
      sanitizedPath += '/';
    }
    return sanitizedPath;
  }

  async createTempFile(content: string | Buffer, fileName: string, useRandom?: boolean) {
    const tempFile = useRandom
      ? `${path.join(process.cwd(), `${crypto.randomBytes(16).toString('hex')}`)}-${fileName}`
      : fileName;

    try {
      await fs.writeFile(tempFile, content, { mode: 0o600 });
      return tempFile;
    } catch {
      throw new Error('AkamaiStorage Error: createTempFile error');
    }
  }

  async removeTempFile(tempFile: string) {
    try {
      await fs.unlink(tempFile);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`AkamaiStorage Error: Failed to cleanup temp file ${tempFile}:`, error);
    }
  }

  async createDirectory(nsPath: string) {
    return new Promise<Response>((resolve, reject) => {
      this.mkdir(nsPath, (err) => {
        if (err) {
          reject(new Error('AkamaiStorage Error: createDirectory failed'));
        } else {
          resolve({ message: 'Directory Created.' });
        }
      });
    });
  }

  async uploadFileAsync(filePath: string, nsFolder?: string) {
    const sanitizedPath = this.sanitizePath(
      nsFolder ? `${this.config.path}${nsFolder}` : this.config.path
    );
    return new Promise<Response & { uploadedFile: string }>((resolve, reject) => {
      this.upload(filePath, sanitizedPath, (err: Error | null, res) => {
        if (err) {
          reject(new Error('AkamaiStorage Error: uploadFileAsync failed'));
        }
        if (res.statusCode !== 200) {
          reject(
            new Error(`AkamaiStorage Error: Netstorageapi returns statusCode: ${res.statusCode}`)
          );
        } else {
          const uploadedFile = this.getAkamaiUrl(path.basename(filePath), nsFolder);
          resolve({
            message: 'Upload Done.',
            uploadedFile
          });
        }
      });
    });
  }

  async uploadBufferAsync({
    buffer,
    fileFullName,
    useHashName,
    nsFolder
  }: {
    buffer: Buffer;
    fileFullName: string;
    useHashName?: boolean;
    nsFolder?: string;
  }) {
    const tempFile = await this.createTempFile(buffer, fileFullName, useHashName);
    try {
      const uploadedResponse = await this.uploadFileAsync(tempFile, nsFolder);
      return uploadedResponse;
    } catch {
      throw new Error('AkamaiStorage Error: uploadBufferAsync failed');
    } finally {
      this.removeTempFile(tempFile);
    }
  }

  async uploadFilesAsync(filePaths: string[], nsFolder?: string) {
    const uploadedFiles: string[] = [];
    const failedFiles: string[] = [];
    for (const filePath of filePaths) {
      try {
        if (!filePath) {
          throw new Error('AkamaiStorage Error: uploadFilesToCDN got an empty file path');
        }
        const { uploadedFile } = await this.uploadFileAsync(filePath, nsFolder);
        uploadedFiles.push(uploadedFile);
      } catch {
        failedFiles.push(filePath);
      }
    }
    return new Promise<Response & { uploadedFiles: string[]; failedFiles: string[] }>((resolve) => {
      resolve({ message: 'Upload Done.', uploadedFiles, failedFiles });
    });
  }
}
export default AkamaiStorage;
